import { Component, EventEmitter, Input, Output } from '@angular/core';

import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { getErpSyncStatusStyles, getQuotationStatusStyles } from 'src/app/shared/utils/quotation-status-styles';
import { IQuotationDetail } from '../../models/quotations.models';
import { ISegregationCheck } from '../../services/segregation-preview.service';

/**
 * The tag strip that sits next to the order name in both order headers — the
 * order detail page and the side-by-side correction panel (WB-7938).
 *
 * Each surface used to hand-roll its own subset of these tags, so they drifted:
 * the status pill was 13px on one and 14px on the other, "Split applies" and
 * "Read-only" only ever rendered in the panel, and the duplicate badge only ever
 * on the detail page. One component now owns the set, the order and the geometry.
 *
 * Visibility stays with the tag, not the host: the only thing a host supplies is
 * the state it alone can know (the split proposal, whether a save is pending).
 */
@Component({
  selector: 'app-order-header-tags',
  templateUrl: './order-header-tags.component.html',
  styleUrls: ['./order-header-tags.component.scss'],
})
export class OrderHeaderTagsComponent {
  constructor(public featureFlags: FeatureFlagService) {}

  @Input() quotation: IQuotationDetail | undefined;

  /**
   * The split proposal currently attached to this order, or null. Owned by the
   * host because each surface sources it differently — `preview-for-partner` on
   * load, the details PATCH response after a save — and clears it after a split.
   */
  @Input() segregationCheck: ISegregationCheck | null = null;

  /**
   * Greys the split tag out and swallows its click. The side-by-side passes its
   * dirty flag: splitting would discard the unsaved edits.
   */
  @Input() splitBlocked = false;

  /** False while the host is still loading the order, or failed to load it. */
  @Input() ready = true;

  @Output() splitClick = new EventEmitter<void>();
  @Output() duplicateDismissed = new EventEmitter<void>();

  get status(): string {
    return this.quotation?.status ?? '';
  }

  get showStatus(): boolean {
    return this.ready && !!this.status;
  }

  /**
   * Records that have reached the ERP are read-only. Computed front-end only,
   * per WB-7478.
   */
  get isReadOnly(): boolean {
    return this.ready && ['done', 'sent_to_erp'].includes(this.status);
  }

  /**
   * WB-7569 — a splitting rule applies and can still be acted on. Suppressed on
   * read-only records: the split has nowhere to land once the order left for the
   * ERP.
   */
  get showSplit(): boolean {
    return this.ready
      && !!this.segregationCheck
      && this.isApprovableFromStatus
      && !this.quotation?.is_archived
      && !this.isReadOnly;
  }

  /**
   * WB-7938 — suppressed on read-only records alongside the split tag: there is
   * nothing left to do about a duplicate once the order reached the ERP.
   */
  get showDuplicate(): boolean {
    return this.ready
      && !!this.quotation?.duplicate_info?.has_duplicates
      && !this.isReadOnly;
  }

  getStatusStyles(): { [key: string]: string } {
    return getQuotationStatusStyles(this.status);
  }

  /**
   * WB-8420 — the ERP sync state moved in here from the detail header's own
   * markup so the side-by-side gets it too: both surfaces now show the same
   * strip, and the sync message below is no longer reachable only from the
   * orders table.
   *
   * Only the states that say something happened earn a tag: in flight
   * (pending/processing), landed (success), or rejected (failed). `none`,
   * `canceled` and `not_applicable` all mean "this order never went to an ERP",
   * which is the default for most of the order's life and not worth a tag next
   * to the order name -- the orders table still shows them in its own column,
   * and a settled failure still raises the error banner on the detail page.
   */
  private static readonly TAGGED_SYNC_STATUSES = ['pending', 'processing', 'success', 'failed'];

  get showErpSync(): boolean {
    return this.featureFlags.asyncErpSyncEnabled
      && this.showStatus
      && OrderHeaderTagsComponent.TAGGED_SYNC_STATUSES.includes(this.erpSyncStatus);
  }

  get erpSyncStatus(): string {
    return this.quotation?.erp_sync?.sync_status || 'none';
  }

  /**
   * WB-8420 — whatever the ERP said about this sync, on every status and not
   * just the failures: a successful sync can still carry a message the BO user
   * needs (a substituted reference, a partial delivery). Same source and
   * precedence as the orders table's own tooltip -- `erp_message` first, the
   * bare `error` reason as the fallback -- so hovering the tag says the same
   * thing wherever it is.
   */
  get erpSyncTooltip(): string {
    const erpSync = this.quotation?.erp_sync;
    return erpSync?.erp_message || erpSync?.error || '';
  }

  getErpSyncStyles(): { [key: string]: string } {
    return getErpSyncStatusStyles(this.erpSyncStatus);
  }

  onSplitClick(): void {
    if (this.splitBlocked) return;
    this.splitClick.emit();
  }

  /** The only two statuses a split proposal can still be applied from. */
  private get isApprovableFromStatus(): boolean {
    return ['to_be_validated', 'pending_inventory'].includes(this.status);
  }
}
